import { Observable } from "rxjs";
import { OrderStatuses } from "src/app/pages/merchant/food-order/models";

export interface RestaurantProfile {
  name: string;
  address: {
    city: string;
    state: string;
    country: string;
    lat: number;
    long: number;
    address: string;
  };
  phoneNumber: string;
  email: string;
  website?: string;
  logoUrl?: string;
  menuIds: string[];
  cuisines: string[];
  timezone: string;
}

export enum MenuStatuses {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface DayHours {
  startHour: string | null; // "HH:MM:SS" format or null
  endHour: string | null; // "HH:MM:SS" format or null
}

export interface Constants {
  orderStatuses: {
    id: OrderStatuses;
    name: string;
  }[];
  timezones: {
    id: string;
    name: string;
  }[];
}

export type MenuProfile = {
  id: string;
  name: string;
  status: MenuStatuses;
  hours: { [key: number]: DayHours | null }; // 0-6, where 0 is Sunday
  categoryId?: string[];
  categories?: Category[];
};

export type MenuShallowProfile = {
  id: string;
  name: string;
  hours: { [key: number]: DayHours | null }; // 0-6, where 0 is Sunday
  status: MenuStatuses;
  categories?: Category[];
};

export type CreateMenuParam = Omit<MenuProfile, "id">;

export interface Category {
  id: string;
  name: string;
  menuIds: string[];
  items: FoodItem[];
}

export interface Menu {
  id: string;
  restaurantId: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryWithMenu {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  menus?: Menu[];
  items?: FoodItemDTO[];
}

export interface CreateCategoryParam {
  name: string;
  menuIds: string[];
}

export interface UpdateRestauranProfileParam {
  name: string;
  address: {
    city: string;
    state: string;
    country: string;
    lat: number;
    long: number;
    address: string;
  };
  phoneNumber: string;
  email: string;
  timezone: string;
  cuisines: string[];
  website?: string;
  logo?: File;
}

export type MenuItemList = {
  categoryId: string;
  categoryName: string;
  items: {
    name: string;
    id: string;
    profilePicture: string;
  }[];
}[];

export enum FoodItemTemperature {
  HOT = "HOT",
  COLD = "COLD",
  WARM = "WARM",
}

export enum PriceBy {
  WEIGHT = "WEIGHT",
  EACH = "EACH",
}

export enum DriaryAttribute {
  VET = "VEGETARIAN",
  VEGAN = "VEGAN",
  GLUTEN_FREE = "GLUTEN_FREE",
}

export interface FoodItem {
  id: string;
  name: string;
  profileImageUrl: string;
  categoryId: string[];
  categories: { id: string; name: string }[];
  price: number;
  priceBy: string;
  vat: number;
  temperature: string; // temparrture config id
  dietaryAttributes: string[];
  outOfStock: boolean;
}

export interface FoodItemDTO {
  id: string;
  name: string;
  description?: string | null;
  ingredients: string | null;
  dietaryAttributes: string | null;
  outOfStock: boolean;
  profileImage?: string | null;
  price: string;
  createdAt?: string;
  updatedAt?: string;
  recipe?: string | null;
  temperature?: string;
  categories?: Array<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }>;
  menus?: Array<{
    id: string;
    name: string;
  }>;
  itemModifiers?: Array<{
    id: string;
    modifierGroupId: string;
    orderIndex: number;
    createdAt: string;
    updatedAt: string;
    modifierGroup?: {
      id: string;
      name: string;
      description: string | null;
      minSelection: number | null;
      maxSelection: number | null;
      isRequired: boolean;
      multipleSelections: boolean;
      maxQuantityOfEachModifier: number | null;
      note: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }>;
}

export type CreateMenuItemParam = Omit<FoodItem, "id" | "profileImageUrl"> & {
  profileImage?: File;
  categoryIds: string[];
};

export type MenuItemConfigParam = {
  foodItemTemperatures: { id: string; name: string }[];
  priceByOptions: { id: string; name: string }[];
  dietaryAttributes: { id: string; name: string }[];
};

export type Modifier = {
  id?: string;
  name: string;
  price: number;
  maxQuantity?: number | null;
  orderIndex?: number;
  createdAt?: Date;
  updatedAt?: Date;
};

export type FoodCustomisation = {
  id: string;
  name: string;
  note: string;
  multipleSelections?: boolean;
  maxSelectionsPerOption?: number;
  minQuantity?: number;
  maxQuantity?: number;
  modifiers?: Modifier[];
};

export type CreateCustomisation = {
  name: string;
  note: string;
};

export type CreateModifier = {
  name: string;
  price: number;
};

export enum RestaurantStatuses {
  OPEN = "OPEN",
  BUSY = "BUSY",
  PAUSED = "PAUSED",
}

export type MerchantCategory = {
  // cuisine
  slug: string;
  name: string;
};

export type RestaurantStatus =
  | { type: RestaurantStatuses.OPEN; remainingTime: null; addingTime: null }
  | { type: RestaurantStatuses.BUSY; addingTime: number; remainingTime: number } // adding tiem in minutes
  | {
      type: RestaurantStatuses.PAUSED;
      remainingTime: number;
      addingTime: null;
    };

export abstract class FoodService {
  abstract getRestaurantProfile(): Promise<RestaurantProfile>;

  abstract saveRestaurantProfile(
    updateData: RestaurantProfile,
    profilePicture?: File,
  ): Promise<void>;

  abstract becomeFoodMerchant(
    restaurantProfle: UpdateRestauranProfileParam,
  ): Promise<void>;
  // abstract switchToNormalMerchant(): Promise<void>;

  abstract createNewMenu(createDto: CreateMenuParam): Promise<MenuProfile>;
  abstract updateMenu(createDto: MenuProfile): Promise<MenuProfile>;

  abstract getMenuDetail(id: string): Observable<MenuProfile>;

  abstract getMenuList(): Observable<Array<MenuShallowProfile>>;
  abstract getCategoryList(): Observable<Array<CategoryWithMenu>>;

  abstract getItemDetail(id: string): Observable<FoodItem | null>;
  abstract updateMenuItem(
    menuItem: CreateMenuItemParam & { id: string },
  ): Observable<FoodItem>;
  abstract createMenuItem(menuItem: CreateMenuItemParam): Observable<FoodItem>;
  abstract checkIfFoodMerchant(): Observable<boolean>;
  abstract getCategoryDetail(id: string): Observable<CategoryWithMenu>;
  abstract getConfiguration(): Observable<MenuItemConfigParam>;

  abstract getFoodItems(params: {
    page: number;
    search?: string;
    limit: number;
    categoryId?: string;
  }): Observable<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodItemDTO[];
  }>;

  abstract addNewCategory(createDto: CreateCategoryParam): Observable<Category>;
  abstract updateCategory(category: Partial<Category>): Observable<Category>;

  abstract getCustomisations(params: {
    page: number;
    search: string;
    limit: number;
  }): Promise<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodCustomisation[];
  }>;

  abstract createCustomisation(
    createCustomisation: CreateCustomisation,
  ): Promise<{
    data: FoodCustomisation;
  }>;

  abstract deleteCustomisation(modifierGroupId: string): Promise<{
    success: boolean;
  }>;

  abstract addModifierToModifierGroup(
    modifierGroupId: string,
    createModifier: CreateModifier,
  ): Promise<Modifier>;

  abstract deleteModifier(
    modifierGroupId: string,
    modifierId: string,
  ): Promise<{
    success: boolean;
  }>;

  abstract updateModifierDisplayOrderIndex(
    modifierGroupId: string,
    modifiers: { id: string; orderIndex: number }[],
  ): Promise<FoodCustomisation>;

  abstract updateModifierGroupRules(
    modifierGroupId: string,
    rules: {
      minSelection: number;
      maxSelection: number;
      multipleSelections: boolean;
      maxQuantityOfEachModifier?: number;
    },
  ): Promise<FoodCustomisation>;

  abstract addCustomisationToitems(
    modifierGroupId: string,
    itemIds: string[],
  ): Promise<FoodCustomisation>;

  abstract updateItemCategoryOrder(
    categoryId: string,
    ids: string[],
  ): Promise<void>;
  abstract updateCategoryOrder(menuId: string, catIds: string[]): Promise<void>;

  abstract updateRestaurantStatus(
    status: RestaurantStatus,
  ): Observable<RestaurantStatus>;

  abstract getRestaurantStatus(): Observable<RestaurantStatus>;

  abstract getConstants(): Observable<Constants>;

  abstract getAllMerchantCategory(): Observable<MerchantCategory[]>;
}
